/**
 * Super Admin Service
 * Handles all Super Admin API calls for company management, subscriptions, and license uploads
 */
import { apiService } from './apiService';

// ============================================
// Types
// ============================================

export interface Company {
  id: number;
  name: string;
  code: string;
  description?: string;
  email?: string;
  phone?: string;
  address?: string;
  registration_number?: string;
  tax_id?: string;
  country?: string;
  corporate_residence?: string;
  plan_type: string;
  max_users: number;
  max_locations: number;
  license_type?: 'micro' | 'macro';
  license_file_path?: string;
  admin_permissions?: string[];
  is_active: boolean;
  subscription_expires_at?: string;
  created_at: string;
  updated_at: string;
}

export interface OwnerData {
  name: string;
  email: string;
  phone: string;
  cid_number: string;
}

export interface CompanyCreate {
  name: string;
  description?: string;
  email?: string;
  phone?: string;
  address?: string;
  country?: string;
  dzongkhag?: string;
  gewog?: string;
  thromde?: string;
  registration_number?: string;
  tax_id?: string;
  plan_type?: string;
  max_users?: number;
  max_locations?: number;
  subscription_period?: 'monthly' | 'yearly';
  license_type?: 'micro' | 'macro';
  ownership_type?: 'sole_proprietorship' | 'partnership' | 'private_limited' | 'public_limited';
  owners?: OwnerData[];  // For multiple owners (partnership, private_limited, public_limited)
  owner_name?: string;  // For single owner (sole_proprietorship)
  owner_email?: string;
  owner_phone?: string;
  owner_cid_number?: string;  // For single owner CID number
  admin_username: string;
  admin_email: string;
  admin_password: string;
  admin_name: string;  // Full name instead of first_name and last_name
  primary_contact: string;  // Primary contact phone number (required)
  remarks?: string;  // Optional remarks/notes
  cid_file: File;
  license_file?: File;
  ownership_document?: File;  // Ownership document / authorization letter
  logo_file?: File;  // Company logo (optional)
}

export interface CompanyStats {
  total_users: number;
  active_users: number;
  total_locations: number;
  active_locations: number;
  total_products: number;
  total_purchase_orders: number;
  total_sales: number;
}

export interface SubscriptionUpdate {
  max_users?: number;
  max_locations?: number;
  plan_type?: string;
  subscription_period?: 'monthly' | 'yearly';
  extend_subscription?: boolean;
  subscription_expires_at?: string;
}

export interface SubscriptionStatus {
  is_active: boolean;
  expires_at?: string;
  days_remaining?: number;
  is_expired: boolean;
}

export interface SuperAdminNotification {
  id: string;
  type: 'company_created' | 'subscription_expiry';
  severity: 'success' | 'warning' | 'error' | 'info';
  title: string;
  message: string;
  company_id?: number;
  company_name?: string;
  subscription_expires_at?: string | null;
  days_remaining?: number | null;
  created_at: string;
}

export interface SuperAdminNotificationsResponse {
  notifications: SuperAdminNotification[];
  unread_count: number;
  emails_sent: number;
}

export interface LicenseUploadResponse {
  success: boolean;
  message: string;
  file_id: string;
  file_path: string;
  extracted_data: {
    name?: string;
    registration_number?: string;
    tax_id?: string;
    address?: string;
    phone?: string;
    email?: string;
    license_type?: string;
    confidence: number;
    extracted_text?: string;
    file_path?: string;
    extraction_method?: string;
  };
  suggestions: {
    company_name?: string;
    registration_number?: string;
    tax_id?: string;
    address?: string;
    phone?: string;
    email?: string;
    code?: string;
    license_type?: string;
    dzongkhag?: string;
    gewog?: string;
    village?: string;
    thromde?: string;
    exact_location?: string;
    activity?: string;
    business_classification?: string;
    scale?: string;
    issuance_date?: string;
    validity_date?: string;
    ownership_type?: string;
    owners?: Array<{ name?: string; cid_number?: string; email?: string; phone?: string }>;
  };
}

// ============================================
// Service Functions
// ============================================

/**
 * Get all companies
 */
export const getCompanies = async (params?: {
  search?: string;
  active_only?: boolean;
}): Promise<Company[]> => {
  return apiService.get<Company[]>('/superadmin/companies', params ? { params } : undefined);
};

/**
 * Get company by ID
 */
export const getCompany = async (companyId: number): Promise<Company> => {
  return apiService.get<Company>(`/superadmin/companies/${companyId}`);
};

/**
 * Get company statistics
 */
export const getCompanyStats = async (companyId: number): Promise<CompanyStats> => {
  return apiService.get<CompanyStats>(`/superadmin/companies/${companyId}/stats`);
};

/**
 * Create company with admin and CID upload
 */
export const createCompanyWithAdminAndCID = async (
  data: CompanyCreate
): Promise<{ message: string; company: Company; admin_user: any; cid_document: any }> => {
  const formData = new FormData();
  
  // Company details (required fields)
  formData.append('name', data.name);
  
  // Optional company fields - only append if they have values
  if (data.description) formData.append('description', data.description);
  if (data.email) formData.append('email', data.email);
  if (data.phone) formData.append('phone', data.phone);
  if (data.address) formData.append('address', data.address);
  if (data.country) formData.append('country', data.country);
  if (data.dzongkhag) formData.append('dzongkhag', data.dzongkhag);
  if (data.gewog) formData.append('gewog', data.gewog);
  if (data.thromde) formData.append('thromde', data.thromde);
  if (data.registration_number) formData.append('registration_number', data.registration_number);
  if (data.tax_id) formData.append('tax_id', data.tax_id);
  
  // Plan and limits - always send with defaults if not provided
  formData.append('plan_type', data.plan_type || 'basic');
  formData.append('max_users', (data.max_users || 10).toString());
  formData.append('max_locations', (data.max_locations || 3).toString());
  
  // Optional subscription and license fields
  formData.append('subscription_period', data.subscription_period || 'yearly');
  if (data.license_type) formData.append('license_type', data.license_type);
  
  // Ownership information
  if (data.ownership_type) {
    formData.append('ownership_type', data.ownership_type);
    
    // For single owner (sole_proprietorship)
    if (data.ownership_type === 'sole_proprietorship') {
      if (data.owner_name) formData.append('owner_name', data.owner_name);
      if (data.owner_email) formData.append('owner_email', data.owner_email);
      if (data.owner_phone) formData.append('owner_phone', data.owner_phone);
      if (data.owner_cid_number) formData.append('owner_cid_number', data.owner_cid_number);
    } else {
      // For multiple owners (partnership, private_limited, public_limited)
      if (data.owners && data.owners.length > 0) {
        // Send owners as JSON string (FastAPI will parse it)
        formData.append('owners', JSON.stringify(data.owners));
      }
    }
  }
  
  // Admin details (required fields)
  formData.append('admin_username', data.admin_username);
  formData.append('admin_email', data.admin_email);
  formData.append('admin_password', data.admin_password);
  formData.append('admin_name', data.admin_name);
  formData.append('primary_contact', data.primary_contact);
  
  // Remarks (optional)
  if (data.remarks) {
    formData.append('remarks', data.remarks);
  }
  
  // CID file (required)
  formData.append('cid_file', data.cid_file);
  
  // License file (required by onboarding)
  if (!data.license_file) {
    throw new Error('License file is required');
  }
  formData.append('license_file', data.license_file);
  
  // Ownership document (optional)
  if (data.ownership_document) {
    formData.append('ownership_document', data.ownership_document);
  }
  
  // Logo file (optional)
  if (data.logo_file) {
    formData.append('logo_file', data.logo_file);
  }
  
  // Don't set Content-Type header - axios will set it automatically with boundary
  // Use 3 min timeout: this endpoint does company + admin + location + file uploads (CID, license, logo) and may send email
  return apiService.post('/superadmin/companies/create-with-admin-and-cid', formData, { timeout: 180000 });
};

/**
 * Update company
 */
export const updateCompany = async (
  companyId: number,
  data: Partial<Company>
): Promise<Company> => {
  return apiService.put<Company>(`/superadmin/companies/${companyId}`, data);
};

/**
 * Delete/deactivate company
 */
export const deleteCompany = async (companyId: number): Promise<void> => {
  return apiService.delete(`/superadmin/companies/${companyId}`);
};

/**
 * Update company subscription
 */
export const updateCompanySubscription = async (
  companyId: number,
  data: SubscriptionUpdate
): Promise<{
  message: string;
  company_id: number;
  company_name: string;
  previous_limits: any;
  new_limits: any;
  current_usage: any;
  subscription_status: SubscriptionStatus;
}> => {
  return apiService.put(`/superadmin/companies/${companyId}/subscription`, data);
};

/**
 * Get Super Admin bell notifications.
 * The backend may send deduped subscription-expiry emails when requested.
 */
export const getSuperAdminNotifications = async (
  sendExpiryEmails = true
): Promise<SuperAdminNotificationsResponse> => {
  return apiService.get<SuperAdminNotificationsResponse>('/superadmin/notifications', {
    params: { send_expiry_emails: sendExpiryEmails },
  });
};

/**
 * Upload license document (stored only; field lookup via license API can be added later)
 */
export const uploadLicense = async (file: File): Promise<LicenseUploadResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  
  return apiService.upload<LicenseUploadResponse>('/superadmin/companies/upload-license', formData);
};

export interface CIDUploadResponse {
  success: boolean;
  message: string;
  file_id: string;
  file_path: string;
  extracted_data: {
    name?: string;
    confidence: number;
    extracted_text?: string;
    file_path?: string;
  };
  suggestions: {
    admin_name?: string;
  };
}

// Analytics Types
export interface AnalyticsOverview {
  total_companies: number;
  active_companies: number;
  inactive_companies: number;
  total_users: number;
  active_users: number;
  total_locations: number;
  active_locations: number;
  total_products: number;
  total_purchase_orders: number;
  total_sales: number;
  companies_this_month: number;
  users_this_month: number;
  monthly_growth_rate: number;
}

export interface CompanyGrowthData {
  month: string;
  new_companies: number;
  total_companies: number;
}

export interface UserActivityData {
  month: string;
  active_users: number;
  new_users: number;
  total_users: number;
}

export interface SubscriptionTrendData {
  month: string;
  active_subscriptions: number;
  expiring_soon: number;
  expired: number;
}

export interface PlanDistribution {
  plan_type: string;
  count: number;
}

export interface LicenseTypeDistribution {
  license_type: string;
  count: number;
}

export interface SubscriptionStatusDistribution {
  status: string;
  count: number;
}

export interface RecentRegistration {
  id: number;
  name: string;
  code: string;
  license_type: string | null;
  is_active: boolean;
  total_users: number;
  total_locations: number;
  created_at: string;
}

export interface AnalyticsResponse {
  overview: AnalyticsOverview;
  company_growth: CompanyGrowthData[];
  user_activity: UserActivityData[];
  subscription_trends: SubscriptionTrendData[];
  plan_distribution: PlanDistribution[];
  license_type_distribution: LicenseTypeDistribution[];
  subscription_status_distribution: SubscriptionStatusDistribution[];
  recent_registrations: RecentRegistration[];
}

/**
 * Upload CID document and extract admin name
 */
export const uploadCID = async (file: File): Promise<CIDUploadResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  
  return apiService.post<CIDUploadResponse>('/superadmin/companies/upload-cid', formData);
};

/**
 * Create additional admin for company
 */
export const createCompanyAdmin = async (
  companyId: number,
  data: {
    username: string;
    email: string;
    password: string;
    first_name: string;
    last_name: string;
  }
): Promise<any> => {
  return apiService.post(`/superadmin/companies/${companyId}/admin`, data);
};

/**
 * Get comprehensive analytics data
 */
export const getAnalytics = async (months: number = 6): Promise<AnalyticsResponse> => {
  return apiService.get<AnalyticsResponse>('/superadmin/analytics', {
    params: { months }
  });
};

const superAdminService = {
  getCompanies,
  getCompany,
  getCompanyStats,
  createCompanyWithAdminAndCID,
  updateCompany,
  deleteCompany,
  updateCompanySubscription,
  getSuperAdminNotifications,
  uploadLicense,
  uploadCID,
  createCompanyAdmin,
  getAnalytics,
};

export default superAdminService;
